import { Injectable } from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { catchError, map, retry } from "rxjs/operators";
import { AppService } from "../../app.service";
import { QueryRef } from "apollo-angular";
import { PromoList } from "../models/model";
import { stateRequest } from "../models/enum";
import { environment } from "src/environments/environment";
import { AffiliatesService } from "../affiliates.service";

@Injectable({
  providedIn: "root",
})
export class PromoListsService {
  baseLambdaUrl = environment.urlAPI;
  baseLambdaAfiliatesUrl = environment.urlAPIAffiliate;

  public token: string;

  baseRetry: number = 0;

  communityQuery: QueryRef<any>;

  constructor(
    private http: HttpClient,
    private _appService: AppService,
    private _affiliateService: AffiliatesService
  ) {}

  getHeaders(): any {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: "Bearer " + this._appService.token,
    });

    return { headers: headers };
  }

  post(url: any, formData: FormData) {
    return this.http.post(url, formData, this.getHeaders());
  }

  put(url: any, formData: FormData) {
    return this.http.put(url, formData, this.getHeaders());
  }

  delete(url: any) {
    return this.http.delete(url, this.getHeaders());
  }

  public stateGetPromoLists: number = stateRequest.notExcuted;
  public promoListsInfo: Array<PromoList> = [];
  public filteredLists: Array<PromoList> = [];
  public _infoPromoListsObservable: Subject<PromoList[]> = new Subject<
    PromoList[]
  >();
  public loadingPromoLists: Boolean = false;
  public _infoPromoListObservable: Subject<{
    method: number;
    list: PromoList;
  }> = new Subject<{ method: number; list: PromoList }>();
  public userPromoList: PromoList;
  public lastEvaluatedKey: any = null;
  public loadingMoreLists: Boolean = false;

  getPromoLists(businessId: string): Observable<any> {
    this.loadingPromoLists = true;
    const url = `${this.baseLambdaAfiliatesUrl}/business/"${businessId}"/lists`;
    return this.http.get(url, this.getHeaders()).pipe(
      map((response: any) => {
        const { status, data } = response;
        this.stateGetPromoLists = status
          ? stateRequest.excuted
          : stateRequest.error;
        if (status) {
          data.forEach((element) => {
            this.promoListsInfo.push(this.setNamesList(element));
          });
          this.deleteRepeatedLists();
          this._infoPromoListsObservable.next(this.promoListsInfo);
          this.filteredLists = this.promoListsInfo;
          this.loadingPromoLists = false;
          this.lastEvaluatedKey = response.LastEvaluatedKey;
        }
        return { valid: status, print: this.promoListsInfo };
      }),
      retry(this.baseRetry),
      catchError(this.handleError<any>("getLists", "error"))
    );
  }

  getMorePromoLists(businessId: string): Observable<any> {
    this.loadingMoreLists = true;
    const url = `${
      this.baseLambdaAfiliatesUrl
    }/business/"${businessId}"/lists?LastEvaluatedKey=${
      JSON.stringify(this.lastEvaluatedKey) || ""
    }`;
    return this.http.get(url, this.getHeaders()).pipe(
      map((response: any) => {
        const { status, data } = response;
        if (status) {
          data.forEach((element) => {
            this.promoListsInfo.push(this.setNamesList(element));
          });
          this.deleteRepeatedLists();
          this._infoPromoListsObservable.next(this.promoListsInfo);
          this.loadingMoreLists = false;
          this.lastEvaluatedKey = response.LastEvaluatedKey;
        }
        return { valid: status, print: this.promoListsInfo };
      }),
      retry(this.baseRetry),
      catchError(this.handleError<any>("getLists", []))
    );
  }

  getPromoList(businessId: string, id: string): Observable<any> {
    const url = `${this.baseLambdaAfiliatesUrl}/business/"${businessId}"/lists?list_id="${id}"`;
    return this.http.get(url, this.getHeaders()).pipe(
      map((response: any) => {
        const { status, data } = response;
        if (status) {
          this.userPromoList = this.setNamesList(data);
          this._infoPromoListObservable.next({
            method: 1,
            list: this.userPromoList,
          });
        }
        return { valid: status, print: this.userPromoList };
      }),
      retry(this.baseRetry),
      catchError(this.handleError<any>("getLists", []))
    );
  }

  createPromoList(businessId: string, data: any): Observable<any> {
    const url = `${this.baseLambdaAfiliatesUrl}/business/"${businessId}"/lists`;
    return this.http.post(url, data, this.getHeaders()).pipe(
      map((response: any) => {
        const { status, data } = response;
        if (status) {
          return { valid: response.status, print: data };
        }
        return { valid: response.status, print: data };
      }),
      retry(this.baseRetry),
      catchError(this.handleError<any>("createLists", []))
    );
  }

  updatePromoList(businessId: string, id: string, data: any): Observable<any> {
    const url = `${this.baseLambdaAfiliatesUrl}/business/"${businessId}"/lists/"${id}"`;
    return this.http.put(url, data, this.getHeaders()).pipe(
      map((response: any) => {
        return { valid: response.status, print: response.data };
      }),
      retry(this.baseRetry),
      catchError(this.handleError<any>("updatePromoList", []))
    );
  }

  deletePromoList(businessId: string, list: any): Observable<any> {
    const url = `${this.baseLambdaAfiliatesUrl}/business/"${businessId}"/lists/"${list}"`;
    return this.http.delete(url, this.getHeaders()).pipe(
      map((response: any) => {
        const { status, data } = response;
        if (status) {
          return { valid: status, print: list };
        }
        return { valid: status, print: data };
      }),
      retry(this.baseRetry),
      catchError(this.handleError<any>("deletePromoList", []))
    );
  }

  cleanPromoLists(): void {
    this.promoListsInfo = [];
    this._infoPromoListsObservable.next(this.promoListsInfo);
    this.stateGetPromoLists = stateRequest.notExcuted;
    this.loadingPromoLists = false;
    this.lastEvaluatedKey = null;
    this.loadingMoreLists = false;
  }

  setNamesList(promoList: PromoList): PromoList {
    let website = this._affiliateService.getWebSites();
    promoList.objects = promoList?.objects || [];
    promoList.objects.forEach((object, index) => {
      //console.log("name", name)
      if (object.entity == "link") {
          promoList.objects[index]["name"] = this._affiliateService.setNames(object);
        promoList.objects[index]["mediaContent"] = {
          id: object.product_id,
          type: object.type,
        };
      } else {
        if (object.entity == "website") {
            const foundWebsite = website.find((w) => w.id == object.id);
            promoList.objects[index]["name"] = foundWebsite?.name;
            promoList.objects[index]["slug"] = foundWebsite?.slug;

        } else if (object.entity == "category") {
          promoList.objects[index]["name"] = this._appService.categories.find(
            (c) => c.id == object.id
          )?.name;
        }
        promoList.objects[index]["mediaContent"] = {
          id: object.id,
          type: object.entity,
        };
      }
    });
    return promoList;
  }

  deleteRepeatedLists(): void {
    let lists = [];
    this.promoListsInfo.forEach((list) => {
      if (lists.findIndex((l) => l.id == list.id) == -1) {
        lists.push(list);
      }
    });
    this.promoListsInfo = lists;
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.log(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
